"use client";

import { useEffect, useMemo, useState } from "react";
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
  type SortingState,
} from "@tanstack/react-table";
import { Icon } from "@/lib/icons/Icon";
import { Field, FormStatus, Input, Select } from "@/components/ui/Form";
import { Badge } from "@/components/ui/Badge/Badge";
import { fetchUserProjects, type UserProject } from "@/lib/api/c-crm-project-management-user";
import styles from "./CCrmProjectManagement.module.css";

const PAGE_SIZE = 20;

const STATE_ALL = "all";
const STATE_ACTIVE = "active";
const STATE_ENDED = "ended";
type StateFilter = typeof STATE_ALL | typeof STATE_ACTIVE | typeof STATE_ENDED;

const STAGE_ALL = "all";

/**
 * Zakładka "Projekty" pluginu c_crm_project_management (shell c_user_accounts).
 *
 * Akcja hash-routable (z `sharingContentForApi`): `projects`. Zakres: projekty
 * w których user jest właścicielem, klientem albo członkiem zespołu - liczy to
 * backend (`IMODEL_M_CRM_PROJECTS::getUserProjects`).
 *
 * **Grid:** TanStack Table v8 (headless) - ten sam wzorzec co lista faktur
 * (`CFactureView`): cały bounded zbiór pobrany raz, sort/filtr/paginacja
 * client-side. Read-only - kolejne fazy dołożą wejście w projekt (zadania,
 * repozytorium dokumentów, dziennik prac).
 */

/** Y-m-d H:i:s -> Y-m-d (data bez czasu). Defensywnie zwraca wejście gdy brak spacji. */
function formatDate(raw: string): string {
  if (!raw) return "";
  return raw.split(" ")[0] || raw;
}

/** Etykieta etapu - nazwa ze słownika, a gdy jej brak, sam numer (bez udawania że etapu nie ma). */
function stageLabel(p: UserProject): string {
  if (p.stage_name) return p.stage_name;
  return p.stage > 0 ? `Etap ${p.stage}` : "-";
}

export function ProjectsList() {
  const [rows, setRows] = useState<UserProject[]>([]);
  const [truncated, setTruncated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [globalFilter, setGlobalFilter] = useState("");
  const [stateFilter, setStateFilter] = useState<StateFilter>(STATE_ACTIVE);
  const [stageFilter, setStageFilter] = useState<string>(STAGE_ALL);
  const [sorting, setSorting] = useState<SortingState>([{ id: "name", desc: false }]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const { projects, truncated: tr } = await fetchUserProjects();
        if (cancelled) return;
        setRows(projects);
        setTruncated(tr);
        setLoading(false);
      } catch (e) {
        if (cancelled) return;
        setError(e instanceof Error ? e.message : String(e));
        setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  /* Lista etapów do selecta - budowana z faktycznych danych, żeby nie pokazywać
     etapów których user nie ma w żadnym projekcie. */
  const stageOptions = useMemo(() => {
    const seen = new Map<number, string>();
    rows.forEach((r) => {
      if (!seen.has(r.stage)) seen.set(r.stage, stageLabel(r));
    });
    return [...seen.entries()].sort((a, b) => a[0] - b[0]);
  }, [rows]);

  const filteredRows = useMemo(() => {
    return rows.filter((r) => {
      if (stateFilter === STATE_ACTIVE && r.ended) return false;
      if (stateFilter === STATE_ENDED && !r.ended) return false;
      if (stageFilter !== STAGE_ALL && String(r.stage) !== stageFilter) return false;
      return true;
    });
  }, [rows, stateFilter, stageFilter]);

  const hasActiveFilters = stateFilter !== STATE_ACTIVE || stageFilter !== STAGE_ALL;

  const columns = useMemo(() => {
    const col = createColumnHelper<UserProject>();
    return [
      col.accessor("nr", { id: "nr", header: () => "Nr" }),
      col.accessor("name", {
        id: "name",
        header: () => "Projekt",
        cell: (ctx) => {
          const r = ctx.row.original;
          return (
            <span className={styles.nameCell}>
              <span className={styles.name}>{ctx.getValue()}</span>
              {r.domain_online && <span className={styles.domain}>{r.domain_online}</span>}
            </span>
          );
        },
      }),
      col.accessor((r) => stageLabel(r), {
        id: "stage",
        header: () => "Etap",
        cell: (ctx) => <Badge>{ctx.getValue()}</Badge>,
      }),
      col.accessor((r) => r.role_name ?? (r.is_client ? "Klient" : "-"), {
        id: "role",
        header: () => "Twoja rola",
      }),
      col.accessor((r) => r.start_date ?? "", {
        id: "start_date",
        header: () => "Start",
        cell: (ctx) => formatDate(ctx.getValue()) || "-",
      }),
      col.accessor((r) => r.deadline_date ?? "", {
        id: "deadline_date",
        header: () => "Deadline",
        cell: (ctx) => formatDate(ctx.getValue()) || "-",
      }),
      col.display({
        id: "state",
        header: () => "Stan",
        cell: (ctx) => {
          const r = ctx.row.original;
          if (r.ended) return <span className={styles.stateEnded}>Zakończony</span>;
          if (r.stop_reason)
            return (
              <span className={styles.stateStopped} title={r.stop_reason}>
                Wstrzymany
              </span>
            );
          if (r.received) return <span className={styles.stateReceived}>Odebrany</span>;
          return <span className={styles.stateActive}>W toku</span>;
        },
      }),
    ];
  }, []);

  const table = useReactTable({
    data: filteredRows,
    columns,
    state: { sorting, globalFilter },
    onSortingChange: setSorting,
    onGlobalFilterChange: setGlobalFilter,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    initialState: { pagination: { pageSize: PAGE_SIZE } },
  });

  const pageCount = table.getPageCount();
  const pageIndex = table.getState().pagination.pageIndex;

  return (
    <div className={styles.root}>
      <p className={styles.note}>Projekty w których uczestniczysz.</p>

      <div className={styles.toolbar}>
        <div className={styles.filters}>
          <Field label="Stan" className={styles.filterField}>
            <Select
              value={stateFilter}
              onChange={(e) => setStateFilter(e.target.value as StateFilter)}
            >
              <option value={STATE_ACTIVE}>Aktywne</option>
              <option value={STATE_ENDED}>Zakończone</option>
              <option value={STATE_ALL}>Wszystkie</option>
            </Select>
          </Field>
          {stageOptions.length > 1 && (
            <Field label="Etap" className={styles.filterField}>
              <Select value={stageFilter} onChange={(e) => setStageFilter(e.target.value)}>
                <option value={STAGE_ALL}>Wszystkie</option>
                {stageOptions.map(([value, label]) => (
                  <option key={value} value={String(value)}>
                    {label}
                  </option>
                ))}
              </Select>
            </Field>
          )}
          {hasActiveFilters && (
            <button
              type="button"
              className={styles.clearFilters}
              onClick={() => {
                setStateFilter(STATE_ACTIVE);
                setStageFilter(STAGE_ALL);
              }}
            >
              <Icon name="x" />
              Wyczyść filtry
            </button>
          )}
        </div>
        <div className={styles.searchWrap}>
          <Input
            type="search"
            prefix={<Icon name="magnifying-glass" />}
            placeholder="Szukaj (nr, nazwa, etap, rola)..."
            value={globalFilter}
            onChange={(e) => setGlobalFilter(e.target.value)}
            aria-label="Szukaj projektów"
          />
        </div>
      </div>

      {loading && <FormStatus variant="info">Wczytywanie projektów...</FormStatus>}
      {error && !loading && <FormStatus variant="error">{error}</FormStatus>}

      {!loading && !error && rows.length === 0 && (
        <FormStatus variant="info">Nie uczestniczysz jeszcze w żadnym projekcie.</FormStatus>
      )}

      {!loading && !error && rows.length > 0 && filteredRows.length === 0 && (
        <FormStatus variant="info">Brak projektów spełniających wybrane filtry.</FormStatus>
      )}

      {!loading && !error && filteredRows.length > 0 && (
        <>
          <div className={styles.tableWrap}>
            <table className={styles.table}>
              <thead>
                {table.getHeaderGroups().map((hg) => (
                  <tr key={hg.id}>
                    {hg.headers.map((header) => {
                      const canSort = header.column.getCanSort();
                      const sorted = header.column.getIsSorted();
                      return (
                        <th
                          key={header.id}
                          className={canSort ? styles.thSortable : undefined}
                          onClick={canSort ? header.column.getToggleSortingHandler() : undefined}
                          aria-sort={
                            sorted === "asc"
                              ? "ascending"
                              : sorted === "desc"
                                ? "descending"
                                : undefined
                          }
                        >
                          <span className={styles.thInner}>
                            {flexRender(header.column.columnDef.header, header.getContext())}
                            {canSort && (
                              <Icon
                                name={sorted === "desc" ? "caret-down" : "caret-up"}
                                className={sorted ? styles.sortIconActive : styles.sortIcon}
                              />
                            )}
                          </span>
                        </th>
                      );
                    })}
                  </tr>
                ))}
              </thead>
              <tbody>
                {table.getRowModel().rows.map((row) => (
                  <tr key={row.id}>
                    {row.getVisibleCells().map((cell) => (
                      <td key={cell.id}>
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {truncated && (
            <p className={styles.truncatedHint}>
              Wyświetlono pierwsze 300 projektów. Zawęź wyszukiwanie aby znaleźć pozostałe.
            </p>
          )}

          {pageCount > 1 && (
            <div className={styles.pagination}>
              <button
                type="button"
                className={styles.pageButton}
                onClick={() => table.previousPage()}
                disabled={!table.getCanPreviousPage()}
              >
                <Icon name="caret-left" />
                Poprzednia
              </button>
              <span className={styles.pageInfo}>
                Strona {pageIndex + 1} z {pageCount}
              </span>
              <button
                type="button"
                className={styles.pageButton}
                onClick={() => table.nextPage()}
                disabled={!table.getCanNextPage()}
              >
                Następna
                <Icon name="caret-right" />
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
